import { useEffect, useState } from 'react'
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom'
import { NotFoundPage } from '@/pages/NotFoundPage'
import i18n, {
  applyLanguage,
  buildLocalizedPath,
  DEFAULT_LANGUAGE,
  isSiteRoot,
  isUrlPrefixLanguage,
  persistLanguage,
  resolveInitialLanguage,
  STORAGE_KEY,
  type Language,
} from '@/lib/i18n'

/**
 * Autoridade única de idioma na árvore pública (D2/D3 do PLANO-I18N).
 *
 * Casado como `<Route path="/:lang?">`, recebe o primeiro segmento da URL:
 * - `ja|en|es`   -> sincroniza o i18next, PERSISTE e renderiza a árvore;
 * - `undefined`  -> PT (a raiz e as rotas públicas sem prefixo);
 * - qualquer outro valor (`/xyz`, `/pt`) -> 404, preservando o comportamento
 *   do catch-all `*` que existia antes do wrapper.
 *
 * A URL vence localStorage e navigator: quem decide aqui é sempre o parâmetro.
 *
 * ── Duas regras que este gate NÃO tinha e que trocavam o idioma do usuário ──
 *
 * 1) **Persistir só o que é escolha.** Antes, `lang === undefined` gravava
 *    `pt` no localStorage. Isso (a) apagava a preferência de quem tinha
 *    escolhido japonês assim que ele caísse numa rota sem prefixo, e (b)
 *    matava o ramo `navigator` de `resolveInitialLanguage` — depois da
 *    primeira visita já havia sempre um `pt` persistido. Agora persistimos
 *    apenas o prefixo explícito de URL (`/ja`, `/en`, `/es`); ausência de
 *    prefixo continua sendo PT para AQUELA página, sem virar preferência.
 *
 * 2) **A raiz negocia o idioma; as demais rotas não.** `/` é o x-default: a
 *    única URL pública que não declara idioma. Chegando nela, aplicamos a
 *    precedência localStorage > navigator > pt e, se o resultado não for PT,
 *    redirecionamos para o prefixo (`/` -> `/ja`). Uma rota pública sem
 *    prefixo (`/diretorio`) é a versão portuguesa POR DEFINIÇÃO e nunca
 *    redireciona — senão um link PT compartilhado abriria em japonês.
 *    Como a precedência começa pelo que está persistido, isto nunca
 *    redireciona contra uma escolha explícita do usuário.
 *
 * O `<Outlet>` só é liberado quando o idioma ALVO já está aplicado no i18next.
 * Sem essa trava, `/en/forum -> /ja/forum` renderizava a página inteira em
 * inglês enquanto `ja.json` carregava. Os dicionários são chunks locais e
 * pequenos, então a espera é de um tick — não há tela branca perceptível.
 */

/** Idioma preferido de quem chega em `/` sem declarar nada na URL. */
function preferredLanguageAtRoot(): Language {
  const stored = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null
  const navigatorLang = typeof navigator !== 'undefined' ? navigator.language : undefined
  return resolveInitialLanguage('/', stored, navigatorLang)
}

export function LocaleGate() {
  const { lang } = useParams<{ lang?: string }>()
  const { pathname, search, hash } = useLocation()

  const prefixed = isUrlPrefixLanguage(lang)
  const target: Language | null = lang === undefined ? DEFAULT_LANGUAGE : prefixed ? lang : null

  // A negociação vale só para a raiz, e não pode entrar em laço: o destino do
  // redirecionamento (`/ja`) já traz prefixo, então `lang !== undefined` na
  // volta e esta condição fica falsa. Voltar a `/` depois de escolher PT no
  // seletor também não redireciona — a escolha ficou persistida e vence o
  // `navigator` dentro de `resolveInitialLanguage`.
  const rootRedirect: Language | null =
    lang === undefined && isSiteRoot(pathname)
      ? (() => {
          const preferred = preferredLanguageAtRoot()
          return preferred === DEFAULT_LANGUAGE ? null : preferred
        })()
      : null

  const [ready, setReady] = useState<boolean>(() => i18n.language === target)

  useEffect(() => {
    if (!target) return
    let cancelled = false
    const sync = () => {
      if (!cancelled) setReady(i18n.language === target)
    }

    sync()
    // Só prefixo de URL é escolha de idioma; ausência de prefixo é a página PT,
    // não uma preferência (ver regra 1 no cabeçalho).
    if (prefixed) persistLanguage(target)
    void applyLanguage(target).finally(sync)
    // Uma troca concorrente (o `setLanguage` do seletor) pode aplicar o idioma
    // por fora desta chamada; o evento do i18next reavalia a liberação.
    i18n.on('languageChanged', sync)
    return () => {
      cancelled = true
      i18n.off('languageChanged', sync)
    }
  }, [target, prefixed])

  if (!target) return <NotFoundPage />
  if (rootRedirect) {
    return <Navigate to={buildLocalizedPath('/', rootRedirect, search, hash)} replace />
  }
  if (!ready) return null

  return <Outlet />
}
